import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { AnimatePresence, motion } from "framer-motion";
import {
  Copy, Trash2, Plus, Save, FileText, GitCompareArrows, RotateCcw, Search,
  MapPin, Building2, Zap, BadgeCheck, AlertTriangle, Loader2, Coins,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, apiErrorMessage } from "@/lib/api";
import {
  FICHES, APPLICATIONS, DEFAULT_PRICING, computeDossier, computeOperation,
  prixSelonVolume, fmt, euros, PRIX_CLIENT_MIN, PRIX_CLIENT_MAX, VERSEMENT_PRIME_OPTIONS,
} from "@/lib/cee";
import { AnimatedNumber, FadeIn } from "@/components/Reveal";
import DevisDocument from "@/components/DevisDocument";

const uid = () => Math.random().toString(36).slice(2, 10);
const newOp = () => ({
  id: uid(),
  fiche: "IND-UT-102",
  marque: "",
  reference: "",
  application: "pompage",
  puissance: "",
  nbMoteurs: "",
});

const INIT_FORM = {
  adresseTravaux: "",
  benefSiren: "",
  clientNom: "",
  benefAdresse: "",
  devisNum: "",
  devisValidite: "30",
  entTel: "",
  entEmail: "",
  commentaire: "",
  prixOverride: false,
  prixMwh: "",
  prixCession: 6,
  versementPrime: "",
  statut: "En création",
};

/* ---------- Adresse avec autocomplétion Base Adresse Nationale ---------- */
const AddrInput = ({ value, onChange }) => {
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const timer = useRef(null);
  const ctrl = useRef(null);

  const onInput = (v) => {
    onChange(v);
    if (timer.current) clearTimeout(timer.current);
    if (v.trim().length < 4) {
      setOpen(false);
      return;
    }
    timer.current = setTimeout(async () => {
      if (ctrl.current) ctrl.current.abort();
      ctrl.current = new AbortController();
      setLoading(true);
      try {
        const res = await fetch(
          `https://api-adresse.data.gouv.fr/search/?limit=5&q=${encodeURIComponent(v)}`,
          { signal: ctrl.current.signal }
        );
        const data = await res.json();
        setSuggestions(data.features || []);
        setOpen(true);
      } catch (e) {
        if (e.name !== "AbortError") setOpen(false);
      } finally {
        setLoading(false);
      }
    }, 260);
  };

  return (
    <div className="relative">
      <input
        data-testid="calc-adresse-input"
        className="field-input pr-9"
        placeholder="Ex. 12 rue de l'Industrie, 69007 Lyon"
        value={value}
        onChange={(e) => onInput(e.target.value)}
      />
      {loading && <Loader2 size={15} className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-[var(--accent-text)]" />}
      {open && (
        <div className="glass absolute z-30 mt-1 max-h-56 w-full overflow-y-auto rounded-xl border border-[var(--line-hover)] shadow-2xl">
          {suggestions.length === 0 && <div className="px-4 py-3 text-xs text-[var(--mute)]">Aucune adresse trouvée.</div>}
          {suggestions.map((f, i) => {
            const p = f.properties || {};
            return (
              <button
                type="button"
                key={i}
                data-testid={`adresse-suggestion-${i}`}
                className="flex w-full items-start gap-2 px-4 py-2.5 text-left text-[13px] text-[var(--ink)] hover:bg-sky-500/10"
                onClick={() => {
                  onChange(p.label || "");
                  setOpen(false);
                }}
              >
                <MapPin size={13} className="mt-0.5 shrink-0 text-[var(--accent-text)]" />
                <span>
                  {p.name || p.label}
                  <span className="ml-2 text-[var(--mute)]">{[p.postcode, p.city].filter(Boolean).join(" ")}</span>
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

/* ---------- Carte opération ---------- */
const OperationCard = ({ op, idx, total, result, onChange, onDuplicate, onDelete }) => {
  const fiche = FICHES[op.fiche] || (op.fiche ? { reference: op.fiche, label: op.fiche, designation: "", applications: [], puissanceMax: null, dureeVie: null, coefficients: {} } : null);
  const isIe4 = op.fiche === "IND-UT-132";
  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 18 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.97 }}
      transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
      className="card-surface p-5"
      data-testid={`operation-card-${idx}`}
    >
      <div className="mb-4 flex items-center justify-between">
        <span className="flex items-center gap-2 text-[11px] font-mono uppercase tracking-[0.2em] text-[var(--accent-text)]">
          <Zap size={13} /> Opération {idx + 1}
        </span>
        <div className="flex gap-2">
          <button type="button" data-testid={`op-duplicate-btn-${idx}`} onClick={onDuplicate} title="Dupliquer"
            className="flex h-8 w-8 items-center justify-center rounded-lg border border-[var(--line)] text-[var(--dim)] transition hover:border-sky-500/40 hover:text-[var(--accent-text)]">
            <Copy size={13} />
          </button>
          <button type="button" data-testid={`op-delete-btn-${idx}`} onClick={onDelete} disabled={total <= 1}
            title={total <= 1 ? "Le dossier doit comporter au moins une opération." : "Supprimer"}
            className="flex h-8 w-8 items-center justify-center rounded-lg border border-[var(--line)] text-[var(--dim)] transition hover:border-rose-500/40 hover:text-[var(--rose-text)] disabled:opacity-30">
            <Trash2 size={13} />
          </button>
        </div>
      </div>

      <div className="mb-4 grid gap-2 sm:grid-cols-2">
        {Object.entries(FICHES).map(([ref, f]) => (
          <button
            type="button"
            key={ref}
            data-testid={`op-fiche-toggle-${idx}-${ref}`}
            onClick={() => onChange({ fiche: ref })}
            className={`rounded-xl border p-3 text-left transition-all ${
              op.fiche === ref
                ? "border-sky-500/60 bg-sky-500/10"
                : "border-[var(--line)] bg-[var(--inset)] hover:border-[var(--line-hover)]"
            }`}
          >
            <span className={`font-mono text-xs font-bold ${op.fiche === ref ? "text-[var(--accent-text)]" : "text-[var(--dim)]"}`}>
              {ref}{" "}
              <span data-testid={`op-fiche-version-${idx}-${ref}`} className="font-normal opacity-70">{f.version}</span>
            </span>
            <span className="mt-0.5 block text-[11px] leading-snug text-[var(--mute)]">{f.shortLabel}</span>
          </button>
        ))}
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <label className="mb-1.5 block text-[11px] font-medium uppercase tracking-wider text-[var(--dim)]">Marque</label>
          <input data-testid={`op-marque-input-${idx}`} className="field-input" placeholder="Ex. Schneider Electric, ABB, Danfoss…"
            value={op.marque} onChange={(e) => onChange({ marque: e.target.value })} />
        </div>
        <div>
          <label className="mb-1.5 block text-[11px] font-medium uppercase tracking-wider text-[var(--dim)]">Référence commerciale / modèle</label>
          <input data-testid={`op-reference-input-${idx}`} className="field-input" placeholder="Ex. Altivar Process ATV900"
            value={op.reference} onChange={(e) => onChange({ reference: e.target.value })} />
        </div>
        {isIe4 ? (
          <div className="sm:col-span-2 rounded-xl border border-[var(--line)] bg-[var(--inset)] px-4 py-3 text-[11px] leading-relaxed text-[var(--mute)]">
            Fiche sans type d'application : le volume est forfaitaire, calculé par la formule officielle selon la puissance utile P du
            moteur (0,12–0,75 kW : 4 900×P+2 600 · 0,75–375 kW : 700×P+12 000 · 375–1 000 kW : 1 600×P). Durée de vie 15 ans (≤ 15 kW)
            ou 20 ans.
          </div>
        ) : (
          <div className="sm:col-span-2">
            <label className="mb-1.5 block text-[11px] font-medium uppercase tracking-wider text-[var(--dim)]">Type d'application du moteur</label>
            <select data-testid={`op-application-select-${idx}`} className="field-input" value={op.application}
              onChange={(e) => onChange({ application: e.target.value })}>
              {Object.entries(APPLICATIONS).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </select>
            <p className="mt-1.5 text-[11px] text-[var(--mute)]">
              Un seul type d'application par opération ; pour plusieurs moteurs identiques, additionnez leurs puissances.
            </p>
          </div>
        )}
        <div>
          <label className="mb-1.5 block text-[11px] font-medium uppercase tracking-wider text-[var(--dim)]">
            {isIe4 ? "Puissance utile par moteur (kW)" : "Puissance nominale totale (kW)"}
          </label>
          <input data-testid={`op-puissance-input-${idx}`} type="number" min="0" step="0.1" className="field-input"
            placeholder="Ex. 45" value={op.puissance} onChange={(e) => onChange({ puissance: e.target.value })} />
          {result.issues.length > 0 && (
            <p className="mt-1.5 text-[11px] text-[var(--amber-text)]">{result.issues.join(" ")}</p>
          )}
          {result.issues.length === 0 && !isIe4 && result.coef > 0 && (
            <p className="mt-1.5 text-[11px] text-[var(--mute)]">Plafond fiche : {fmt(fiche.puissanceMax)} kW · durée de vie {fiche.dureeVie} ans</p>
          )}
        </div>
        <div>
          <label className="mb-1.5 block text-[11px] font-medium uppercase tracking-wider text-[var(--dim)]">
            {isIe4 ? "Nombre de moteurs concernés" : "Nombre de moteurs (optionnel)"}
          </label>
          <input data-testid={`op-nbmoteurs-input-${idx}`} type="number" min="0" step="1" className="field-input"
            placeholder="Ex. 3" value={op.nbMoteurs} onChange={(e) => onChange({ nbMoteurs: e.target.value })} />
          <p className="mt-1.5 text-[11px] text-[var(--mute)]">
            {isIe4 ? "Multiplie le volume de la fiche (moteurs identiques)." : "Mentionné sur l'estimation ; sans effet sur le calcul."}
          </p>
        </div>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-x-6 gap-y-2 rounded-xl border border-[var(--line-soft)] bg-[var(--inset)] px-4 py-3 text-xs">
        {isIe4 ? (
          <span className="text-[var(--mute)]">Volume forfaitaire (fiche IE4)</span>
        ) : (
          <span className="text-[var(--mute)]">Coefficient : <b className="text-[var(--ink)]">{result.coef ? fmt(result.coef) : "—"} kWh cumac/kW</b></span>
        )}
        <span className="text-[var(--mute)]">kWh cumac : <b className="text-[var(--ink)]">{result.kwhCumac ? fmt(Math.round(result.kwhCumac)) : "—"}</b></span>
        <span className="ml-auto text-[var(--mute)]">Prime : <b className="text-[var(--accent-text)]">{euros(result.prime)}</b></span>
      </div>
    </motion.div>
  );
};

/* ---------- Page calculateur ---------- */
const Calculateur = () => {
  const { isAdmin } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const editId = searchParams.get("edit");
  const [pricing, setPricing] = useState(DEFAULT_PRICING);
  const [form, setForm] = useState(INIT_FORM);
  const [ops, setOps] = useState([newOp()]);
  const [saving, setSaving] = useState(false);
  const [devis, setDevis] = useState(null);
  const [benefNote, setBenefNote] = useState(null);

  const setF = (patch) => setForm((f) => ({ ...f, ...patch }));
  const setOp = (id, patch) => setOps((list) => list.map((o) => (o.id === id ? { ...o, ...patch } : o)));

  useEffect(() => {
    api.get("/pricing").then((d) => setPricing(d.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!editId) return;
    api
      .get(`/dossiers/${editId}`)
      .then(({ data }) => {
        setForm({
          ...INIT_FORM,
          ...data,
          prixMwh: data.prixMwh ?? "",
          nbMoteurs: undefined,
        });
        setOps(
          (data.operations || []).map((o) => ({ ...newOp(), ...o, puissance: o.puissance ?? "", nbMoteurs: o.nbMoteurs ?? "" }))
        );
        if (!data.operations?.length) setOps([newOp()]);
      })
      .catch((e) => toast.error(apiErrorMessage(e)));
  }, [editId]);

  const { results, totals } = useMemo(
    () => computeDossier(ops, form, pricing),
    [ops, form, pricing]
  );

  const buildPayload = () => ({
    nom: form.clientNom || "",
    statut: form.statut || "En création",
    adresseTravaux: form.adresseTravaux,
    benefSiren: form.benefSiren,
    clientNom: form.clientNom,
    benefAdresse: form.benefAdresse,
    devisNum: form.devisNum,
    devisValidite: form.devisValidite,
    entTel: form.entTel,
    entEmail: form.entEmail,
    commentaire: form.commentaire || "",
    prixOverride: isAdmin && form.prixOverride,
    prixMwh: isAdmin && form.prixOverride ? parseFloat(form.prixMwh) || totals.prixAuto : null,
    prixCession: parseFloat(form.prixCession) || PRIX_CLIENT_MAX,
    versementPrime: form.versementPrime,
    operations: ops.map(({ fiche, marque, reference, application, puissance, nbMoteurs, kwhCumac }) => ({
      fiche, marque, reference, application,
      puissance: parseFloat(puissance) || 0,
      nbMoteurs: nbMoteurs === "" ? null : parseFloat(nbMoteurs),
      kwhCumac: kwhCumac ?? null,
    })),
  });

  const saveDossier = async () => {
    if (!form.versementPrime) {
      toast.error("Choisissez le mode de versement de la prime (direct bénéficiaire ou remise sur devis) avant d'enregistrer.");
      return null;
    }
    setSaving(true);
    try {
      const payload = buildPayload();
      const { data } = editId
        ? await api.put(`/dossiers/${editId}`, payload)
        : await api.post("/dossiers", payload);
      toast.success(`Dossier « ${data.nom} » ${editId ? "mis à jour" : "enregistré"}.`, { description: `${fmt(data.totals.mwhCumacTotal, 1)} MWh cumac · ${euros(data.totals.primeNette)}` });
      if (!editId) setSearchParams({ edit: data.id }, { replace: true });
      return data;
    } catch (e) {
      toast.error(apiErrorMessage(e));
      return null;
    } finally {
      setSaving(false);
    }
  };

  const generateDevis = async () => {
    let num = form.devisNum.trim();
    if (!num) {
      const { data: all } = await api.get("/dossiers");
      num = `EST-${new Date().getFullYear()}-${String(all.length + 1).padStart(3, "0")}`;
      setF({ devisNum: num });
    }
    const saved = await saveDossier();
    if (!saved) return;
    setDevis({ ...saved, devisNum: num });
  };

  const addSimulation = async () => {
    try {
      const payload = buildPayload();
      const { data } = await api.post("/simulations", {
        nom: form.clientNom || `Simulation ${new Date().toLocaleDateString("fr-FR")}`,
        prixOverride: payload.prixOverride,
        prixMwh: payload.prixMwh,
        prixCession: payload.prixCession,
        operations: payload.operations,
      });
      toast.success("Simulation ajoutée au comparateur.");
      return data;
    } catch (e) {
      toast.error(apiErrorMessage(e));
    }
  };

  const resetAll = () => {
    setForm(INIT_FORM);
    setOps([newOp()]);
    setSearchParams({}, { replace: true });
    toast.info("Nouveau dossier prêt.");
  };

  const lookupSiren = async () => {
    const raw = (form.benefSiren || "").replace(/\D/g, "");
    if (raw.length !== 9) {
      setBenefNote({ text: "Le numéro SIREN doit comporter 9 chiffres.", kind: "warn" });
      return;
    }
    setBenefNote({ text: "Recherche en cours…", kind: "" });
    try {
      const res = await fetch(`https://recherche-entreprises.api.gouv.fr/search?q=${raw}&per_page=1`);
      const data = await res.json();
      const r = data?.results?.[0];
      if (!r) {
        setBenefNote({ text: "Aucune entreprise trouvée pour ce SIREN — saisie manuelle possible.", kind: "warn" });
        return;
      }
      const nom = r.nom_complet || r.nom_raison_sociale || r.sigle || "";
      const s = r.siege || {};
      const adresse = s.adresse || [s.numero_voie, s.type_voie, s.libelle_voie].filter(Boolean).join(" ") +
        [s.code_postal, s.libelle_commune].filter(Boolean).length
        ? [s.adresse, [s.code_postal, s.libelle_commune].filter(Boolean).join(" ")].filter(Boolean).join(", ")
        : "";
      const patch = {};
      if (nom) patch.clientNom = nom;
      if (adresse) patch.benefAdresse = adresse;
      setF(patch);
      setBenefNote({ text: `Informations récupérées${nom ? ` (${nom})` : ""} — modifiables si besoin.`, kind: "ok" });
    } catch (e) {
      setBenefNote({ text: "Annuaire indisponible : saisissez la raison sociale et l'adresse manuellement.", kind: "warn" });
    }
  };

  const prixHint = totals.prixOverride
    ? `Tarif automatique pour ce volume : ${fmt(totals.prixAuto, 1)} € — prix actuel fixé manuellement.`
    : "Tarif automatique appliqué selon le volume du dossier.";

  return (
    <div className="mx-auto max-w-[1440px] px-4 pb-24 sm:px-6">
      {/* En-tête */}
      <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="mb-2 text-[11px] font-mono uppercase tracking-[0.24em] text-[var(--accent-text)]">Calculateur de prime CEE</p>
          <h1 className="font-display text-3xl font-extrabold tracking-tight text-[var(--ink)] sm:text-4xl">
            {editId ? "Modifier le dossier" : "Nouveau dossier de prime"}
          </h1>
        </div>
        {editId && (
          <button data-testid="new-dossier-btn" onClick={resetAll} className="btn-ghost">
            <RotateCcw size={14} /> Nouveau dossier
          </button>
        )}
      </div>

      <div className="grid gap-6 lg:grid-cols-[1fr_400px]">
        {/* ------- Colonne formulaire ------- */}
        <div className="space-y-6">
          {/* Dossier */}
          <FadeIn className="card-surface p-6">
            <h2 className="font-display mb-5 flex items-center gap-2 text-lg font-bold text-[var(--ink)]">
              <Building2 size={17} className="text-[var(--accent-text)]" /> Dossier
            </h2>
            <div className="space-y-3">
              <AddrInput value={form.adresseTravaux} onChange={(v) => setF({ adresseTravaux: v })} />
              <p className="text-[11px] text-[var(--mute)]">
                Utilisée pour identifier le dossier et pré-remplir l'adresse du devis ; sans effet sur le calcul.
              </p>
            </div>
          </FadeIn>

          {/* Versement de la prime — choix obligatoire avant enregistrement */}
          <FadeIn className="card-surface p-6">
            <h2 className="font-display mb-1 flex items-center gap-2 text-lg font-bold text-[var(--ink)]">
              <Coins size={17} className="text-[var(--accent-text)]" /> Versement de la prime
            </h2>
            <p className="mb-4 text-[13px] text-[var(--mute)]">Choix obligatoire avant l'enregistrement : à qui la prime CEE sera-t-elle versée ?</p>
            <div className="grid gap-3 sm:grid-cols-2">
              {VERSEMENT_PRIME_OPTIONS.map((o) => (
                <button
                  type="button"
                  key={o.value}
                  data-testid={`versement-option-${o.value}`}
                  onClick={() => setF({ versementPrime: o.value })}
                  className={`rounded-xl border p-4 text-left transition-all ${
                    form.versementPrime === o.value
                      ? "border-sky-500/60 bg-sky-500/10"
                      : "border-[var(--line)] bg-[var(--inset)] hover:border-[var(--line-hover)]"
                  }`}
                >
                  <span className={`block text-sm font-semibold ${form.versementPrime === o.value ? "text-[var(--accent-text)]" : "text-[var(--ink)]"}`}>{o.label}</span>
                  <span className="mt-1.5 block text-[11px] leading-relaxed text-[var(--mute)]">{o.hint}</span>
                </button>
              ))}
            </div>
          </FadeIn>

          {/* Opérations */}
          <div>
            <div className="mb-4 flex items-center justify-between">
              <h2 className="font-display text-lg font-bold text-[var(--ink)]">
                Opérations du dossier <span className="ml-2 rounded-full bg-sky-500/12 px-2.5 py-0.5 font-mono text-xs text-[var(--accent-text)]">{ops.length}</span>
              </h2>
              <button
                data-testid="add-operation-btn"
                onClick={() => setOps((l) => [...l, newOp()])}
                className="btn-ghost"
              >
                <Plus size={14} /> Ajouter une opération
              </button>
            </div>
            <p className="mb-4 text-[13px] text-[var(--mute)]">
              Un même dossier peut regrouper plusieurs opérations (fiches, moteurs, variateurs).
            </p>
            <div className="space-y-4">
              <AnimatePresence mode="popLayout">
                {ops.map((op, idx) => (
                  <OperationCard
                    key={op.id}
                    op={op}
                    idx={idx}
                    total={ops.length}
                    result={results[idx]}
                    onChange={(patch) => setOp(op.id, patch)}
                    onDuplicate={() => setOps((l) => [...l.slice(0, idx + 1), { ...op, id: uid() }, ...l.slice(idx + 1)])}
                    onDelete={() => setOps((l) => (l.length > 1 ? l.filter((o) => o.id !== op.id) : l))}
                  />
                ))}
              </AnimatePresence>
            </div>
          </div>

          {/* Bénéficiaire */}
          <FadeIn className="card-surface p-6">
            <h2 className="font-display mb-5 flex items-center gap-2 text-lg font-bold text-[var(--ink)]">
              <BadgeCheck size={17} className="text-[var(--accent-text)]" /> Information bénéficiaire
            </h2>
            <div className="grid gap-3 sm:grid-cols-[220px_1fr]">
              <div>
                <label className="mb-1.5 block text-[11px] font-medium uppercase tracking-wider text-[var(--dim)]">
                  Bénéficiaire — n° SIREN
                </label>
                <div className="flex gap-2">
                  <input
                    data-testid="calc-siren-input"
                    className="field-input font-mono"
                    placeholder="123456789"
                    value={form.benefSiren}
                    onChange={(e) => setF({ benefSiren: e.target.value.replace(/\D/g, "").slice(0, 9) })}
                    onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), lookupSiren())}
                  />
                  <button type="button" data-testid="siren-lookup-btn" onClick={lookupSiren} className="btn-ghost shrink-0 px-3">
                    <Search size={14} />
                  </button>
                </div>
              </div>
              <div>
                <label className="mb-1.5 block text-[11px] font-medium uppercase tracking-wider text-[var(--dim)]">Raison sociale</label>
                <input data-testid="calc-client-nom-input" className="field-input" placeholder="Ex. ACME Industries SAS"
                  value={form.clientNom} onChange={(e) => setF({ clientNom: e.target.value })} />
              </div>
              <div className="sm:col-span-2">
                <label className="mb-1.5 block text-[11px] font-medium uppercase tracking-wider text-[var(--dim)]">Adresse du bénéficiaire</label>
                <input data-testid="calc-benef-adresse-input" className="field-input" placeholder="Adresse du siège du bénéficiaire"
                  value={form.benefAdresse} onChange={(e) => setF({ benefAdresse: e.target.value })} />
              </div>
            </div>
            {benefNote && (
              <p className={`mt-3 text-[12px] ${benefNote.kind === "ok" ? "text-[var(--accent-text)]" : "text-[var(--amber-text)]"}`}>{benefNote.text}</p>
            )}
          </FadeIn>

          {/* Devis */}
          <FadeIn className="card-surface p-6">
            <h2 className="font-display mb-5 flex items-center gap-2 text-lg font-bold text-[var(--ink)]">
              <FileText size={17} className="text-[var(--accent-text)]" /> Devis & contact
            </h2>
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <label className="mb-1.5 block text-[11px] font-medium uppercase tracking-wider text-[var(--dim)]">N° d'estimation (auto si vide)</label>
                <input data-testid="calc-devis-num-input" className="field-input font-mono" placeholder="DEV-2026-001"
                  value={form.devisNum} onChange={(e) => setF({ devisNum: e.target.value })} />
              </div>
              <div>
                <label className="mb-1.5 block text-[11px] font-medium uppercase tracking-wider text-[var(--dim)]">Validité (jours)</label>
                <input data-testid="calc-devis-validite-input" type="number" min="1" className="field-input"
                  value={form.devisValidite} onChange={(e) => setF({ devisValidite: e.target.value })} />
              </div>
              <div>
                <label className="mb-1.5 block text-[11px] font-medium uppercase tracking-wider text-[var(--dim)]">Téléphone (devis)</label>
                <input data-testid="calc-ent-tel-input" className="field-input" placeholder="01 23 45 67 89"
                  value={form.entTel} onChange={(e) => setF({ entTel: e.target.value })} />
              </div>
              <div>
                <label className="mb-1.5 block text-[11px] font-medium uppercase tracking-wider text-[var(--dim)]">Email (devis)</label>
                <input data-testid="calc-ent-email-input" className="field-input" placeholder="contact@thionnet-si.fr"
                  value={form.entEmail} onChange={(e) => setF({ entEmail: e.target.value })} />
              </div>
            </div>
          </FadeIn>
        </div>

        {/* ------- Rail résultats (sticky) ------- */}
        <div className="lg:sticky lg:top-24 lg:self-start">
          <div className="card-surface glow-brand overflow-hidden">
            <div className="border-b border-[var(--line)] bg-gradient-to-br from-sky-500/12 to-transparent p-6">
              <p className="text-[11px] font-mono uppercase tracking-[0.24em] text-[var(--accent-text)]">Prime CEE estimée (nette)</p>
              <div data-testid="calc-prime-nette" className="font-display mt-2 text-5xl font-extrabold tracking-tight text-[var(--ink)]">
                <AnimatedNumber value={Math.round(totals.primeNette)} suffix=" €" />
              </div>
              <p className="mt-2 text-xs text-[var(--dim)]" data-testid="calc-prime-sub">
                {fmt(totals.mwhCumacTotal, 1)} MWh cumac × {fmt(parseFloat(form.prixCession) || PRIX_CLIENT_MAX, 2)} €/MWh
              </p>
            </div>

            <div className="grid grid-cols-3 divide-x divide-[var(--line)] border-b border-[var(--line)] text-center">
              {[
                ["Opérations", fmt(totals.nbOperations), "calc-out-nbop"],
                ["Puissance", `${fmt(totals.puissanceTotale, 1)} kW`, "calc-out-puissance"],
                ["kWh cumac", fmt(Math.round(totals.kwhCumacTotal)), "calc-out-kwh"],
              ].map(([l, v, tid]) => (
                <div key={l} className="px-3 py-4">
                  <div data-testid={tid} className="font-display text-lg font-bold text-[var(--ink)]">{v}</div>
                  <div className="mt-0.5 text-[10px] uppercase tracking-wider text-[var(--mute)]">{l}</div>
                </div>
              ))}
            </div>

            <div className="space-y-5 p-6">
              {/* Valorisation du MWh cumac — curseur 5,3 à 6 €/MWh */}
              <div>
                <div className="mb-2 flex items-center justify-between">
                  <label className="text-[11px] font-mono uppercase tracking-widest text-[var(--dim)]">Valorisation du MWh cumac</label>
                  <span data-testid="calc-valorisation-display" className="font-mono text-xs text-[var(--accent-text)]">
                    {fmt(parseFloat(form.prixCession) || PRIX_CLIENT_MAX, 2)} €/MWh
                  </span>
                </div>
                <input
                  data-testid="calc-valorisation-slider"
                  type="range" min={PRIX_CLIENT_MIN} max={PRIX_CLIENT_MAX} step="0.05"
                  value={parseFloat(form.prixCession) || PRIX_CLIENT_MAX}
                  onChange={(e) => setF({ prixCession: e.target.value })}
                  className="w-full"
                />
                <div className="mt-1.5 flex justify-between text-[10px] font-mono text-[var(--mute)]">
                  <span>5,30 €</span>
                  <span>6,00 €</span>
                </div>
                <p className="mt-1.5 text-[11px] leading-relaxed text-[var(--mute)]">
                  Votre commission d'apport d'affaires :{" "}
                  <b data-testid="calc-commission-value" className="text-[var(--accent-text)]">{euros(Math.round(totals.commissionTotal))}</b>{" "}
                  ({fmt(PRIX_CLIENT_MAX - (parseFloat(form.prixCession) || PRIX_CLIENT_MAX), 2)} €/MWh) — la prime ci-dessus est celle
                  versée au bénéficiaire.
                </p>
              </div>

              {/* Panneau interne — visible uniquement de l'administrateur */}
              {isAdmin && (
                <div data-testid="admin-internal-panel" className="space-y-3 rounded-xl border border-amber-500/25 bg-amber-500/6 p-4">
                  <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-[var(--amber-text)]">Interne — masqué au client</p>
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-[11px] uppercase tracking-wider text-[var(--dim)]">Prix de rachat réel</span>
                    <label className="flex cursor-pointer items-center gap-2 text-[11px] text-[var(--dim)]">
                      <input
                        type="checkbox"
                        data-testid="admin-prix-override-toggle"
                        checked={!!form.prixOverride}
                        onChange={(e) => setF({ prixOverride: e.target.checked, prixMwh: e.target.checked ? String(totals.prixAuto) : "" })}
                        className="h-3.5 w-3.5 accent-sky-500"
                      />
                      Fixer
                    </label>
                  </div>
                  {form.prixOverride ? (
                    <input
                      data-testid="admin-prix-input"
                      type="number" min="0" step="0.1"
                      className="field-input font-mono"
                      value={form.prixMwh}
                      onChange={(e) => setF({ prixMwh: e.target.value })}
                    />
                  ) : (
                    <div data-testid="calc-prix-display" className="rounded-xl border border-[var(--line)] bg-[var(--inset)] px-4 py-2.5 font-mono text-sm text-[var(--ink)]">
                      {fmt(totals.prixApplique, 1)} € / MWh <span className="text-[var(--mute)]">(auto : {fmt(totals.prixAuto, 1)} €)</span>
                    </div>
                  )}
                  <p className="text-[11px] leading-relaxed text-[var(--mute)]">
                    Prime au prix réel : <b className="text-[var(--dim)]">{euros(Math.round(totals.primeBrute))}</b> · marge interne :{" "}
                    <b className="text-[var(--dim)]">{euros(Math.round(totals.margeInterne))}</b>. {prixHint}
                  </p>
                </div>
              )}

              {/* Éligibilité */}
              {totals.eligible ? (
                <div data-testid="calc-eligibility-ok" className="flex items-start gap-2.5 rounded-xl border border-emerald-500/30 bg-emerald-500/8 px-4 py-3 text-[12px] leading-relaxed text-[var(--green-text)]">
                  <BadgeCheck size={15} className="mt-0.5 shrink-0" />
                  Conditions d'éligibilité réunies pour l'ensemble des {ops.length} opération(s) de ce dossier (sous réserve des autres
                  critères de la fiche applicable : réalisation par un professionnel, justificatifs requis, etc.).
                </div>
              ) : (
                <div data-testid="calc-eligibility-ko" className="rounded-xl border border-amber-500/30 bg-amber-500/8 px-4 py-3 text-[12px] leading-relaxed text-[var(--amber-text)]">
                  <p className="mb-1 flex items-center gap-2 font-semibold"><AlertTriangle size={14} /> Points de vigilance</p>
                  <ul className="list-inside list-disc space-y-0.5 text-[var(--amber-text)] opacity-90">
                    {totals.motifs.map((m, i) => <li key={i}>{m}</li>)}
                  </ul>
                </div>
              )}

              {/* Actions */}
              <div className="space-y-2.5 pt-1">
                <button data-testid="calc-save-dossier-btn" onClick={saveDossier} disabled={saving} className="btn-primary w-full py-3">
                  {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />}
                  {editId ? "Mettre à jour le dossier" : "Enregistrer le dossier"}
                </button>
                <div className="grid grid-cols-2 gap-2.5">
                  <button data-testid="calc-devis-btn" onClick={generateDevis} className="btn-ghost py-2.5">
                    <FileText size={14} /> Générer l'estimation
                  </button>
                  <button data-testid="calc-add-sim-btn" onClick={addSimulation} className="btn-ghost py-2.5">
                    <GitCompareArrows size={14} /> Comparer
                  </button>
                </div>
                {editId && (
                  <button data-testid="calc-reset-btn" onClick={resetAll} className="btn-ghost w-full py-2.5 text-[var(--dim)]">
                    <RotateCcw size={14} /> Réinitialiser la saisie
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {devis && <DevisDocument dossier={devis} onClose={() => setDevis(null)} />}
    </div>
  );
};

export default Calculateur;
